import { ChoiceGroup, Icon, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from "@fluentui/react";
import React from "react";
import CustomPanel from "../../../../SharedComponents/CustomPanel/CustomPanel";
import { AI_PLAYGROUND_CONSTANTS as CONSTANTS } from "../../AIPlayground.constants";
import { SessionsPanelProps } from "../../Interfaces/Sessions.interfaces";
import { sessionClassNames as classNames, sessionStyles as styles } from "../../Styles";
import { createChoiceFieldRenderer } from "../Common";

export const SessionsPanel: React.FC<SessionsPanelProps> = ({
  deleted,
  handleEditSession,
  handleRunClick,
  isDeleting,
  isSaved,
  isSaving,
  isSessionDataEditable,
  onClosePanel,
  onDeleteButtonClick,
  onFieldUpdate,
  onSaveButtonClick,
  openPanel,
  selectedRowData
}) => {
  const scheduleOptions = React.useMemo(
    () =>
      CONSTANTS.STATUSES.map((option) => ({
        key: option,
        text: option.charAt(0).toUpperCase() + option.slice(1),
        onRenderField: createChoiceFieldRenderer(classNames.scaledChoiceFieldStyle)
      })),
    [classNames.scaledChoiceFieldStyle]
  );
  const frequencyOptions = React.useMemo(
    () =>
      CONSTANTS.SCHEDULE_FREQUENCY_OPTIONS.map((f) => ({
        key: f.key,
        text: f.text,
        onRenderField: createChoiceFieldRenderer(classNames.scaledChoiceFieldStyle)
      })),
    [classNames.scaledChoiceFieldStyle]
  );

  return (
    <CustomPanel
      dismissPanel={() => onClosePanel()}
      enablePanel={openPanel}
      isLoading={false}
      panelTitle={
        <span>
          Session Details
          <Icon
            iconName="Edit"
            style={{ marginLeft: 12, fontSize: 18, cursor: "pointer" }}
            onClick={() => handleEditSession()}
            title={isSessionDataEditable ? "Disable Edit Mode" : "Enable Edit Mode"}
          />
        </span>
      }
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <TextField
          label="Title"
          styles={isSessionDataEditable ? styles.textFieldStyles : styles.readonlyTextFieldStyles}
          resizable={false}
          value={selectedRowData?.title || ""}
          readOnly={!isSessionDataEditable}
          onChange={(_, value) => onFieldUpdate("title", value ?? "")}
        />
        <TextField
          label="Description"
          multiline
          rows={4}
          styles={isSessionDataEditable ? styles.textFieldStyles : styles.readonlyTextFieldStyles}
          resizable={false}
          value={selectedRowData?.description || ""}
          readOnly={!isSessionDataEditable}
          onChange={(_, value) => onFieldUpdate("description", value ?? "")}
        />
        <TextField
          label="Kusto Query"
          multiline
          resizable={false}
          rows={7}
          styles={isSessionDataEditable ? styles.textFieldStyles : styles.readonlyTextFieldStyles}
          value={selectedRowData?.query || ""}
          readOnly={!isSessionDataEditable}
          onChange={(_, value) => onFieldUpdate("query", value ?? "")}
        />
        <TextField
          label="Prompt"
          multiline
          resizable={false}
          rows={7}
          styles={isSessionDataEditable ? styles.textFieldStyles : styles.readonlyTextFieldStyles}
          value={selectedRowData?.prompt || ""}
          readOnly={!isSessionDataEditable}
          onChange={(_, value) => onFieldUpdate("prompt", value ?? "")}
        />
        <ChoiceGroup
          label="Status"
          options={scheduleOptions}
          styles={styles.choiceGroupStyles}
          selectedKey={selectedRowData?.isActiveSchedule === true ? "active" : "inactive"}
          onChange={(_, option) => {
            onFieldUpdate("isActiveSchedule", option?.key === "active");
          }}
          value={selectedRowData?.isActiveSchedule ? "active" : "inactive"}
          name="isActiveSchedule"
          disabled={!isSessionDataEditable}
        />
        <ChoiceGroup
          label="Schedule Frequency"
          options={frequencyOptions}
          styles={styles.choiceGroupStyles}
          selectedKey={selectedRowData?.scheduleFrequency}
          onChange={(_, option) => onFieldUpdate("scheduleFrequency", option?.key ?? "")}
          value={selectedRowData?.scheduleFrequency}
          disabled={!selectedRowData?.isActiveSchedule || !isSessionDataEditable}
          name="scheduleFrequency"
        />
        <div className={classNames.panelButtonStyles}>
          <PrimaryButton
            iconProps={{ iconName: "OpenInNewWindow" }}
            styles={styles.buttonStyles}
            onClick={() => handleRunClick("open")}
          >
            Open
          </PrimaryButton>
          <PrimaryButton
            iconProps={{ iconName: "Save" }}
            styles={styles.buttonStyles}
            onClick={() => onSaveButtonClick()}
            disabled={!isSessionDataEditable || isSaving || isSaved || isDeleting}
          >
            {(() => {
              if (isSaving)
                return (
                  <>
                    <span style={{ marginRight: 8 }}>Saving</span>
                    <Spinner size={SpinnerSize.xSmall} />;
                  </>
                );
              if (isSaved) return <>Saved successfully</>;
              return <>Save</>;
            })()}
          </PrimaryButton>
          <PrimaryButton
            iconProps={{ iconName: "Delete" }}
            styles={styles.buttonStyles}
            onClick={() => onDeleteButtonClick()}
            disabled={!isSessionDataEditable || isSaving || isSaved || isDeleting}
          >
            {(() => {
              if (isDeleting)
                return (
                  <>
                    <span style={{ marginRight: 8 }}>Deleting</span>
                    <Spinner size={SpinnerSize.xSmall} />;
                  </>
                );
              if (deleted) return <>Deleted successfully</>;
              return <>Delete</>;
            })()}
          </PrimaryButton>
        </div>
      </Stack>
    </CustomPanel>
  );
};
